import { Engine } from 'engine/Engine';
import { Entity } from 'engine/Entity';
import { ColliderFacet, ColliderType } from 'engine/facets/ColliderFacet';
import { Facet } from 'engine/facets/Facet';
import { SpriteGroupFacet } from 'engine/facets/SpriteGroupFacet';
import { StaticSpriteFacet } from 'engine/facets/StaticSpriteFacet';
import { AudioManager } from 'engine/lib/AudioManager';
import { newV2 } from 'engine/lib/V2';
import { create } from 'engine/lib/create';
import { Hittable } from 'game/behaviors/Hittable';
import { Laserable } from 'game/behaviors/Laserable';
import { CollisionLayer } from 'game/config';
import { easeOutQuart, lerp } from 'game/lib/easing';
import { TreeBottomSprite, TreeShadow, TreeTopSprite } from 'game/sprites/TreeSprite';
import { TreeState } from 'game/state/RootState';
import clamp from 'lodash/clamp';
import { BulletEntity } from './BulletEntity';

const treeFallSrc = require('game/sfx/tree-fall.mp3');

const treeFall = new AudioManager(treeFallSrc, { volume: 0.6 });

interface TreeEntityProps {
  treeState: TreeState,
}

class TreeFallBehavior extends Facet<TreeEntityProps> {

  static FALL_DURATION = 1700;

  fallStart = 0;
  factor = 1;

  hit = (entity: Entity, { time }: Engine) => {
    if (entity instanceof BulletEntity) {
      this.props.treeState.chopped = true;
      this.fallStart = time.now;
      const hitFromRight = entity.transform.position > this.entity.transform.position;
      this.factor = hitFromRight ? -1 : 1;
      this.treeTopSprite.props.transformOrigin = hitFromRight ? '170 950' : '210 950';
      this.treeFallParts.forEach(f => this.entity.removeFacet(f));
      treeFall.clone().play();
      return true;
    }
    return false;
  };

  treeTopSprite = create(SpriteGroupFacet, {
    children: [create(StaticSpriteFacet, { frame: TreeTopSprite })],
  });

  treeFallParts = [
    create(Laserable, { didSucceed: () => this.props.treeState.chopped }),
    create(Hittable, { hit: this.hit }),
    create(ColliderFacet, { type: ColliderType.Box,
      size: [50, 90],
      offset: [-10, -160],
      trigger: true,
      layer: CollisionLayer.Hitbox,
    }),
  ];

  start(engine: Engine) {
    this.entity.addFacet(this.treeTopSprite, engine);
    this.treeFallParts.forEach(f => this.entity.addFacet(f, engine));
    this.hit(this.entity, engine);
  }

  update(delta: number, engine: Engine): void {
    if (!this.fallStart) return;
    const progress = clamp((engine.time.now - this.fallStart) / TreeFallBehavior.FALL_DURATION, 0, 1);
    if (progress === 1) {
      // HACK: Safari randomly forgets about the transform origin.
      // Somehow this forces Safari to remember that it exists.
      const { transform } = this.treeTopSprite;
      transform.rotation = ((transform.rotation === 96 * this.factor) ? 96.00000001 : 96) * this.factor;
      return;
    }
    const a = 0.1;
    const b = 1 - a;
    if (progress < a) {
      this.treeTopSprite.transform.rotation = lerp(0, 2 * this.factor, easeOutQuart(progress / a));
    } else {
      this.treeTopSprite.transform.rotation = lerp(
        2 * this.factor,
        96 * this.factor,
        ((progress - a) / b) ** 5,
      );
    }
  }
}

export class TreeEntity extends Entity<TreeEntityProps> {

  createFacets(props: TreeEntityProps) {
    const facets: Facet[] = [
      create(StaticSpriteFacet, { frame: TreeShadow }),
      create(StaticSpriteFacet, { frame: TreeBottomSprite }),
      create(ColliderFacet, { type: ColliderType.Circle, radius: 24, offset: newV2(-13, -40) }),
    ];
    if (!props.treeState.chopped) {
      facets.push(create(TreeFallBehavior, props));
    }

    return facets;
  }
}
